// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import capitalize from "lodash/capitalize";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationPaymentMethodStyles {
    container?: InjectableCss;
    paymentMethodTitleText?: TypographyPresentationProps;
    paymentMethodText?: TypographyPresentationProps;
    // XNG-Changes
    paymentTitle?: TypographyPresentationProps;
    purchaseOrderTitleText?: TypographyPresentationProps;
    purchaseOrderText?: TypographyPresentationProps;
}

export const paymentMethodStyles: OnePageCheckoutConfirmationPaymentMethodStyles = {
    container: {
        css: css`
            flex-direction: column;
            margin-top: 15px;
        `,
    },
    paymentMethodTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = paymentMethodStyles;

export const getPaymentMethodValueDisplay = (cart: Cart) => {
    const { cardType, lastFour, expirationDate } = cart.properties;
    const cardTypeDisplay = capitalize(cardType || "");

    if ((cart.paymentMethod?.isCreditCard || cart.paymentMethod?.isPaymentProfile) && cardType && lastFour && expirationDate) {
        return translate(`{0} **** {1}    Exp. {2}`, cardTypeDisplay, lastFour, expirationDate);
    }

    return cart.paymentMethod?.description || "";
};

const OnePageCheckoutConfirmationPaymentMethod = ({ cart }: Props) => {
    if (!cart || (!cart.paymentOptions?.creditCard && !cart.poNumber)) {
        return null;
    }

    const paymentMessage = getPaymentMethodValueDisplay(cart);
    return (
        <StyledWrapper {...styles.container}>
            {paymentMessage && (
                <>
                    <Typography as="h3" {...styles.paymentTitle}>
                        {translate("Payment")}
                    </Typography>
                    <StyledWrapper>
                        <Typography {...styles.paymentMethodTitleText}>{translate("Credit Card")}</Typography>
                        <Typography {...styles.paymentMethodText}>{paymentMessage}</Typography>
                    </StyledWrapper>
                </>
            )}
            {cart.poNumber && (
                <StyledWrapper>
                    <Typography {...styles.purchaseOrderTitleText}>{translate("Purchase Order #")}</Typography>
                    <Typography {...styles.purchaseOrderText}>{cart.poNumber}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationPaymentMethod),
    definition: {
        displayName: "Payment Method",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
